import { queryOptions, useQuery } from '@tanstack/react-query';
import { api, apiList } from '@/lib/api-client';
import { USERS_PAGE_SIZE } from '@erp/shared';
import type {
  Appointment,
  Catalog,
  CatalogDetail,
  Client,
  ClientsOperatedInErp,
  DeskListItem,
  DeskDetail,
  QuoteMapConfig,
  TicketCharge,
  TicketQuoteList,
  TicketComment,
  TicketDetail,
  TicketEvent,
  TicketFile,
  TicketGroup,
  TicketListItem,
  TicketSearchResult,
  TifluxTicketPending,
  TicketCloseOptions,
  User,
  UserKind,
} from '@erp/shared';

// spec 066: a forma dos parâmetros mora com os montadores da lista de uma mesa
import type { TicketsListParams } from './ticket-list-params';
export type { TicketsListParams } from './ticket-list-params';

export function ticketsListQuery(params: TicketsListParams) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'list', params] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<TicketListItem>('/tickets', { query: { ...params }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** Busca em todas as mesas visíveis, agrupada por mesa (spec 012). */
export function ticketSearchQuery(q: string) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'search', { q }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TicketSearchResult>('/tickets/search', { query: { q }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function ticketDetailQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'detail', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TicketDetail>(`/tickets/${ticketNumber}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function ticketCommentsQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'comments', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<TicketComment>(`/tickets/${ticketNumber}/comments`, { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}

export function ticketAppointmentsQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'appointments', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<Appointment>(`/tickets/${ticketNumber}/appointments`, { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}

/**
 * GET /tickets/:n/tiflux-pendings (spec 026, FR-005) — o que mudou no TiFlux e
 * o ERP tem outro valor, campo a campo. Só existe para ticket fora do espelho;
 * o indicador do detalhe só pergunta nesses.
 */
export function ticketTifluxPendingsQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'tiflux-pendings', { ticketNumber }] as const,
    queryFn: async () => {
      const res = await api<TifluxTicketPending[]>(`/tickets/${ticketNumber}/tiflux-pendings`);
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * O que o diálogo de fechamento oferece (spec 054): os motivos ativos da mesa
 * do ticket, e se escolher é obrigatório. Sob ['erp', 'tickets']: mexer nos
 * motivos de uma mesa invalida isto junto.
 */
export function ticketCloseOptionsQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'close-options', { ticketNumber }] as const,
    queryFn: async () => {
      const res = await api<TicketCloseOptions>(`/tickets/${ticketNumber}/close-reasons`);
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function ticketEventsQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'events', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<TicketEvent>(`/tickets/${ticketNumber}/events`, { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}

// ── Lançamentos financeiros do ticket (v9) ──────────────────────────────────
// Contrato do @erp/shared (campos achatados, resposta array puro).

export function ticketChargesQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'charges', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TicketCharge[]>(`/tickets/${ticketNumber}/charges`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

// ── Cotação de deslocamento (PoolTrack, 04/10) ──────────────────────────────
// Lista + se o cálculo de km está disponível (sem provedor: só km à mão).

export function ticketQuotesQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'quotes', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TicketQuoteList>(`/tickets/${ticketNumber}/quotes`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * O mini mapa da cotação (F-112): a chave do NAVEGADOR da Maps JavaScript API,
 * pedida à API quando o diálogo abre — nunca no build. Erro (inclusive 404 de
 * uma API anterior a esta rota) vira "sem mapa", e o diálogo segue igual. Sem
 * retentativa: a chave não aparece numa segunda tentativa.
 */
export function quoteMapConfigQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'quotes', 'map', { ticketNumber }] as const,
    queryFn: async ({ signal }): Promise<QuoteMapConfig> => {
      const res = await api<QuoteMapConfig>(`/tickets/${ticketNumber}/quotes/map`, { signal });
      if (res.error) return { browserKey: null, mapId: null };
      return res.data;
    },
    retry: false,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
  });
}

/** Anexos do ticket (soltos e de comentários — commentId distingue). */
export function ticketFilesQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'files', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<TicketFile>(`/tickets/${ticketNumber}/files`, { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}

// ── Lookups from other domains (same key convention, shared cache) ──────────

export function desksListQuery() {
  return queryOptions({
    queryKey: ['erp', 'desks', 'list', { limit: 100 }] as const,
    queryFn: async ({ signal }) => {
      // a linha da LISTA (DeskListItem): + o que o disparo da mesa exige (spec 031)
      const res = await apiList<DeskListItem>('/desks', { query: { limit: 100 }, signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
    staleTime: 60_000,
  });
}

export function deskDetailQuery(deskId: number) {
  return queryOptions({
    queryKey: ['erp', 'desks', 'detail', { deskId }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<DeskDetail>(`/desks/${deskId}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
  });
}

/** Catálogos de serviço vinculados à mesa (vazio ⇒ mesa sem catálogo). */
export function deskCatalogsQuery(deskId: number) {
  return queryOptions({
    queryKey: ['erp', 'desks', 'catalogs', { deskId }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<Catalog>(`/desks/${deskId}/catalogs`, { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
    staleTime: 60_000,
  });
}

/** Árvore completa do catálogo (catálogo → áreas → itens). */
export function catalogTreeQuery(catalogId: number) {
  return queryOptions({
    queryKey: ['erp', 'catalogs', 'detail', { catalogId }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<CatalogDetail>(`/catalogs/${catalogId}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
  });
}

/**
 * Grupos de tickets (v11) — leitura liberada para staff (o select do detalhe e
 * o filtro da lista precisam), escrita gated por desks.manage na tela de
 * configuração.
 */
export function ticketGroupsQuery() {
  return queryOptions({
    queryKey: ['erp', 'ticket-groups', 'list'] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<TicketGroup>('/ticket-groups', { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
    staleTime: 60_000,
  });
}

/**
 * Grupos selecionáveis num select/filtro: os ativos + o grupo ATUAL da seleção
 * mesmo se inativado depois (regra client-side — a lista completa vem da query
 * acima, sem `?active=`, justamente para o atual inativo continuar aqui).
 */
export function selectableTicketGroups(
  groups: TicketGroup[],
  currentId?: number
): TicketGroup[] {
  return groups.filter((g) => g.active || g.id === currentId);
}

/**
 * `GET /users` — as pessoas DESTE tenant, equipe E logins de portal das
 * associações (F-108), uma página por vez, com o total. É da tela de usuários
 * e do contador dela, e de mais ninguém: seletor, filtro e nome de pessoa da
 * equipe usam `teamListQuery`/`TeamMemberPicker` (features/team), que só
 * trazem a equipe, sem o portal. A guarda de fonte (API,
 * shared/team-picker-source.test.ts) reprova quem voltar a esta.
 *
 * Até F-108 era a tabela global inteira cortada em 200 por nome — a tela
 * mostrava 200 e o contador dizia 200, com mais de 270 pessoas só na PoolTrack.
 * Agora a busca e a página são do servidor, e o total vem do `X-Total-Items`.
 */
export function usersListQuery(params: {
  q: string;
  page: number;
  pageSize?: number;
  /** spec 034: a aba da tela de usuários (equipe ou portal); ausente = todos */
  kind?: UserKind;
}) {
  const pageSize = params.pageSize ?? USERS_PAGE_SIZE;
  const kind = params.kind;
  return queryOptions({
    queryKey: ['erp', 'users', 'list', { q: params.q, page: params.page, pageSize, kind }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<User>('/users', {
        query: {
          q: params.q.trim() || undefined,
          limit: pageSize,
          offset: (params.page - 1) * pageSize,
          kind,
        },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
  });
}

/**
 * Os clientes operados no ERP (spec 024) — desligados do espelho do TiFlux. A
 * mesa espelhada só recebe ticket deles (e ele nasce fora do espelho), então é
 * esta lista que decide se a tela de criação oferece as mesas `tiflux`.
 */
export function operatedClientsQuery() {
  return queryOptions({
    queryKey: ['erp', 'clients', 'operated-in-erp'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ClientsOperatedInErp>('/clients/operated-in-erp', { signal });
      if (res.error) throw res.error;
      return res.data.clientIds;
    },
    staleTime: 60_000,
  });
}

/** o conjunto dos operados no ERP; vazio enquanto carrega (nada a oferecer) */
export function useOperatedClientIds(): ReadonlySet<number> {
  const q = useQuery(operatedClientsQuery());
  return new Set(q.data ?? []);
}

/**
 * As mesas onde dá para abrir ticket (spec 011 → 024): as do ERP sempre; as
 * espelhadas só para cliente operado no ERP. Sem cliente escolhido, a mesa
 * espelhada entra quando há ALGUM operado — o seletor de cliente restringe.
 */
export function creatableDesks<D extends { active: boolean; origin: 'tiflux' | 'erp' }>(
  desks: readonly D[],
  operated: ReadonlySet<number>,
  clientId: number | null
): D[] {
  const tifluxOk = clientId === null ? operated.size > 0 : operated.has(clientId);
  return desks.filter((d) => d.active && (d.origin === 'erp' || tifluxOk));
}

export function clientsSearchQuery(search: string) {
  return queryOptions({
    queryKey: ['erp', 'clients', 'list', { search, limit: 20 }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<Client>('/clients', { query: { search, limit: 20 }, signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}
